import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMsg, colones } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Combobox } from "@/components/Combobox";
import { CotizacionRow } from "@/components/CotizacionRow";
import { Plus, FolderOpen, Trash2 } from "lucide-react";

export default function Cotizaciones() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [cots, setCots] = useState([]);
  const [anteriores, setAnteriores] = useState([]);
  const [todas, setTodas] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [modelos, setModelos] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [modeloId, setModeloId] = useState("");
  const [referencia, setReferencia] = useState("");
  const [notas, setNotas] = useState("");
  const [colores, setColores] = useState([]);
  const [codigoColor, setCodigoColor] = useState("sin");
  const [creando, setCreando] = useState(false);

  const cargar = useCallback(async () => {
    const { data } = await api.get("/trabajos/cotizaciones", { params: { todas } });
    setCots(data);
  }, [todas]);
  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    api.get("/cotizaciones", { params: { todas, solo_informales: true } }).then((r) => setAnteriores(r.data)).catch(() => {});
  }, [todas]);

  useEffect(() => {
    if (dialog && !modelos.length) {
      api.get("/modelos").then((r) => setModelos(r.data)).catch(() => {});
      api.get("/catalogo-referencia/lotes").then((r) => setLotes(r.data)).catch(() => {});
    }
  }, [dialog, modelos.length]);

  const modeloGrupos = useMemo(() => {
    const esHonda = (m) => {
      const marca = (m.marca || "").toUpperCase();
      return marca ? marca === "HONDA" : (m.nombre_mostrar || "").toUpperCase().startsWith("HONDA");
    };
    const conCatalogo = (m) => lotes.some((l) => (l.modelo_ids || []).includes(m.id));
    const ordenar = (arr) => [...arr].sort((a, b) => (a.nombre_mostrar || "").localeCompare(b.nombre_mostrar || ""));
    const aOpciones = (arr) => ordenar(arr).map((m) => ({ value: m.id, label: m.nombre_mostrar }));
    return [
      { heading: "Honda con catálogo", options: aOpciones(modelos.filter((m) => esHonda(m) && conCatalogo(m))) },
      { heading: "Honda sin catálogo", options: aOpciones(modelos.filter((m) => esHonda(m) && !conCatalogo(m))) },
      { heading: "Otras marcas", options: aOpciones(modelos.filter((m) => !esHonda(m))) },
    ];
  }, [modelos, lotes]);

  useEffect(() => {
    setCodigoColor("sin");
    if (!modeloId) { setColores([]); return; }
    api.get(`/modelos/${modeloId}/colores`).then((r) => setColores(r.data)).catch(() => setColores([]));
  }, [modeloId]);

  const crear = async () => {
    setCreando(true);
    try {
      const { data } = await api.post("/trabajos/cotizacion", {
        modelo_id: modeloId, referencia, notas,
        codigo_color: codigoColor === "sin" ? "" : codigoColor,
      });
      setDialog(false); setModeloId(""); setReferencia(""); setNotas(""); setCodigoColor("sin");
      navigate(`/cotizaciones/c/${data.id}`);
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setCreando(false); }
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar esta cotización?")) return;
    try { await api.delete(`/trabajos/${id}`); toast.success("Cotización eliminada"); cargar(); }
    catch (e) { toast.error(errorMsg(e)); }
  };

  const eliminarAnterior = async (id) => {
    if (!window.confirm("¿Eliminar esta cotización?")) return;
    try {
      await api.delete(`/cotizaciones/${id}`);
      toast.success("Cotización eliminada");
      setAnteriores((prev) => prev.filter((c) => c.id !== id));
    } catch (e) { toast.error(errorMsg(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold">Mis cotizaciones</h1>
          <p className="text-sm text-muted-foreground">{cots.length} cotizaciones guardadas</p>
        </div>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <Switch data-testid="ver-todas-switch" checked={todas} onCheckedChange={setTodas} />
              Ver todas
            </label>
          )}
          <Button data-testid="nueva-cotizacion-btn" onClick={() => setDialog(true)}>
            <Plus size={15} className="mr-1" /> Nueva cotización
          </Button>
        </div>
      </div>

      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary">
              <TableHead>Número</TableHead>
              <TableHead>Modelo / Referencia</TableHead>
              <TableHead className="hidden sm:table-cell">Fecha</TableHead>
              <TableHead className="hidden md:table-cell">Mano de obra</TableHead>
              <TableHead className="w-24"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cots.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">
                Aún no tienes cotizaciones. Crea la primera con el botón "Nueva cotización".
              </TableCell></TableRow>
            )}
            {cots.map((c) => (
              <TableRow key={c.id} data-testid={`cotizacion-trabajo-row-${c.id}`} className="cursor-pointer"
                onClick={() => navigate(`/cotizaciones/c/${c.id}`)}>
                <TableCell className="font-mono text-xs font-semibold">COT-{String(c.numero || 0).padStart(4, "0")}</TableCell>
                <TableCell className="font-medium">
                  {c.modelo || "—"}
                  {c.referencia && <span className="ml-1.5 text-xs text-muted-foreground">· {c.referencia}</span>}
                  {c.codigo_color && (
                    <span className="ml-1.5 text-xs text-muted-foreground">
                      · {c.codigo_color}{c.color_nombre ? ` ${c.color_nombre}` : ""}
                    </span>
                  )}
                </TableCell>
                <TableCell className="hidden sm:table-cell text-muted-foreground text-xs">
                  {c.fecha_creacion ? c.fecha_creacion.slice(0, 10).split("-").reverse().join("/") : ""}
                </TableCell>
                <TableCell className="hidden md:table-cell font-semibold">{colones(c.total_mano_obra)}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-1">
                    <Button data-testid={`cotizacion-trabajo-abrir-${c.id}`} variant="ghost" size="icon" className="h-7 w-7"
                      title="Abrir" onClick={() => navigate(`/cotizaciones/c/${c.id}`)}><FolderOpen size={14} /></Button>
                    <Button data-testid={`cotizacion-trabajo-eliminar-${c.id}`} variant="ghost" size="icon"
                      className="h-7 w-7 text-destructive" title="Eliminar" onClick={() => eliminar(c.id)}><Trash2 size={14} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {anteriores.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">Cotizaciones anteriores</h2>
          <div className="bg-white border border-border rounded-sm overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary">
                  <TableHead>Nombre / referencia</TableHead>
                  <TableHead className="hidden sm:table-cell">Fecha</TableHead>
                  {todas && <TableHead className="hidden sm:table-cell">Usuario</TableHead>}
                  <TableHead className="hidden md:table-cell">Repuestos</TableHead>
                  <TableHead className="hidden md:table-cell">Mano de obra</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {anteriores.map((c) => (
                  <CotizacionRow key={c.id} c={c} todas={todas} soloAbrir
                    onAbrir={() => navigate(`/cotizaciones/${c.id}`)}
                    onEliminar={() => eliminarAnterior(c.id)} />
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Nueva cotización</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Modelo *</Label>
              <Combobox testId="cotizacion-modelo-select" value={modeloId} onChange={setModeloId}
                placeholder="Seleccione un modelo..." searchPlaceholder="Buscar modelo..."
                groups={modeloGrupos} />
            </div>
            {colores.length > 0 && (
              <div className="space-y-1">
                <Label>Código de color (opcional)</Label>
                <Select value={codigoColor} onValueChange={setCodigoColor}>
                  <SelectTrigger data-testid="cotizacion-color-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sin">Sin especificar</SelectItem>
                    {colores.map((c) => (
                      <SelectItem key={c.codigo} value={c.codigo}>
                        {c.nombre ? `${c.codigo} · ${c.nombre}` : c.codigo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <Label>Referencia</Label>
              <Input data-testid="cotizacion-referencia-input" value={referencia} placeholder="Ej: nombre o teléfono del cliente"
                onChange={(e) => setReferencia(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Notas</Label>
              <Textarea data-testid="cotizacion-notas-crear-input" value={notas} placeholder="Notas (opcional)"
                onChange={(e) => setNotas(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancelar</Button>
            <Button data-testid="crear-cotizacion-btn" onClick={crear} disabled={!modeloId || creando}>
              {creando ? "Creando..." : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
